import { Landmark, Hash, Wallet, Network, ShieldCheck } from "lucide-react";

const CATEGORY = "Konten & Identität";

export const ACCOUNTS = [
  {
    slug: "geschaeftskonten",
    name: "Geschäftskonten",
    category: CATEGORY,
    icon: Landmark,
    tagline: "Programmierbare Geschäftskonten mit dediziertem Ledger.",
    overline: "Geschäftskonten",
    hero: {
      title: "Vollwertige Geschäftskonten, vollständig programmierbar.",
      subtitle:
        "Eröffnen Sie dedizierte Konten mit eigener IBAN, Sub-Ledger und Multi-Entity-Struktur – in Sekunden über eine einzige API statt über Wochen im Filialprozess.",
    },
    stats: [
      { value: "< 60s", label: "Kontoeröffnung" },
      { value: "1:N", label: "Sub-Ledger je Entity" },
      { value: "24/7", label: "Echtzeit-Salden" },
    ],
    features: [
      { icon: Landmark, title: "Dedizierte IBANs", desc: "Jedes Konto erhält eine eigene, adressierbare IBAN mit vollständiger SEPA- und SWIFT-Erreichbarkeit." },
      { icon: Network, title: "Sub-Ledger", desc: "Beliebig tiefe Kontenhierarchien für Mandanten, Abteilungen oder Endkunden – sauber getrennt und auditierbar." },
      { icon: ShieldCheck, title: "Freigabe-Workflows", desc: "Rollenbasierte Berechtigungen, Vier-Augen-Prinzip und Limits direkt am Konto durchgesetzt." },
      { icon: Wallet, title: "Echtzeit-Ledger", desc: "Jede Buchung ist sofort, deterministisch und unveränderlich im zentralen Ledger sichtbar." },
    ],
    architecture: {
      caption: "Konten-Layer über dem CashFUX Ledger Core",
      layers: [
        { label: "Zugriff", nodes: [{ label: "Dashboard", sub: "UI" }, { label: "REST API", sub: "v1" }, { label: "Webhooks", sub: "Events" }] },
        { label: "Konten-Engine", nodes: [{ label: "Entities", sub: "Mandanten" }, { label: "Sub-Ledger", sub: "Hierarchie" }, { label: "Limits", sub: "Policy" }] },
        { label: "Ledger Core", nodes: [{ label: "Double-Entry", sub: "immutable" }, { label: "IBAN-Register", sub: "SEPA/SWIFT" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/accounts",
      code: [
        "// Geschäftskonto für eine Entity anlegen",
        "const account = await cashfux.accounts.create({",
        '  type: "business",',
        '  currency: "EUR",',
        '  entity: "acme-gmbh",',
        "});",
        "",
        "// → IBAN, Ledger & Webhook sofort aktiv",
        "account.iban // DE89 3704 0044 0532 0130 00",
      ],
      highlights: [
        "Idempotente Erstellung – kein Doppelkonto bei Retries",
        "Sub-Ledger per parent_id verschachtelbar",
        "Webhook account.created bei jedem Event",
      ],
    },
    useCases: [
      { title: "Marktplätze", desc: "Je Verkäufer ein Sub-Konto mit eigener IBAN und automatischem Settlement." },
      { title: "Holding-Strukturen", desc: "Getrennte Ledger je Tochtergesellschaft, konsolidiert in einer Ansicht." },
      { title: "SaaS-Plattformen", desc: "Kunden-Konten programmatisch bereitstellen und abrechnen." },
    ],
    faq: [
      { q: "Sind die IBANs echt und SEPA-fähig?", a: "Ja. Jedes Konto erhält eine dedizierte, voll erreichbare IBAN für ein- und ausgehende SEPA- sowie SWIFT-Zahlungen." },
      { q: "Wie tief lassen sich Sub-Ledger verschachteln?", a: "Beliebig. Sie modellieren Ihre Organisationsstruktur 1:1 über parent_id-Referenzen." },
      { q: "Wie funktioniert die Reconciliation?", a: "Jede Buchung ist im Double-Entry-Ledger sofort sichtbar; Salden sind in Echtzeit deterministisch." },
      { q: "Kann ich Limits pro Konto setzen?", a: "Ja, über Policy-Objekte auf Konto- oder Entity-Ebene, inklusive Freigabe-Workflows." },
    ],
    related: ["iban", "wallets", "kyc-compliance"],
  },
  {
    slug: "iban",
    name: "IBAN",
    category: CATEGORY,
    icon: Hash,
    tagline: "Dedizierte und virtuelle IBANs auf Abruf.",
    overline: "IBAN",
    hero: {
      title: "IBANs als API-Ressource.",
      subtitle:
        "Erzeugen Sie dedizierte oder virtuelle IBANs programmatisch – zur automatischen Zuordnung eingehender Zahlungen ohne Verwendungszweck-Chaos.",
    },
    stats: [
      { value: "Virtuell", label: "vIBAN je Kunde" },
      { value: "SEPA", label: "Instant & SCT" },
      { value: "100%", label: "Auto-Reconciliation" },
    ],
    features: [
      { icon: Hash, title: "Virtuelle IBANs", desc: "Eine eindeutige vIBAN pro Endkunde – eingehende Zahlungen werden automatisch zugeordnet." },
      { icon: Network, title: "Auto-Reconciliation", desc: "Kein Verwendungszweck-Parsing mehr: Der Zahlungseingang landet direkt am richtigen Ledger." },
      { icon: Landmark, title: "SEPA & SWIFT", desc: "Voll erreichbar für SCT, SCT Instant und internationale SWIFT-Überweisungen." },
      { icon: ShieldCheck, title: "Namenskonformität", desc: "IBAN-Name-Check und Sanktionsprüfung vor Gutschrift integriert." },
    ],
    architecture: {
      caption: "Zuordnung eingehender Zahlungen über virtuelle IBANs",
      layers: [
        { label: "Eingang", nodes: [{ label: "SEPA SCT", sub: "inbound" }, { label: "SCT Instant", sub: "10s" }, { label: "SWIFT", sub: "MT103" }] },
        { label: "vIBAN-Router", nodes: [{ label: "Matching", sub: "Auto" }, { label: "Name-Check", sub: "AML" }] },
        { label: "Ledger", nodes: [{ label: "Gutschrift", sub: "Echtzeit" }, { label: "Webhook", sub: "payment.received" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/ibans",
      code: [
        "// Virtuelle IBAN für einen Endkunden anlegen",
        "const viban = await cashfux.ibans.create({",
        '  account: "acc_9f2",',
        '  type: "virtual",',
        '  reference: "customer_4821",',
        "});",
        "",
        "// Eingang wird automatisch zugeordnet",
        "viban.value // DE22 1001 0100 0000 0421 88",
      ],
      highlights: [
        "1:1 Zuordnung Kunde ↔ virtuelle IBAN",
        "Kein Verwendungszweck-Matching nötig",
        "Webhook payment.received mit Referenz",
      ],
    },
    useCases: [
      { title: "Debitoren-Zuordnung", desc: "Jeder Kunde zahlt auf seine eigene vIBAN – Zuordnung ohne manuelle Nacharbeit." },
      { title: "Mieteinnahmen", desc: "Eine IBAN je Mietvertrag für lückenlose Abstimmung." },
      { title: "Wallet-Aufladung", desc: "Eingehende Zahlung lädt automatisch das passende Wallet auf." },
    ],
    faq: [
      { q: "Was unterscheidet virtuelle von dedizierten IBANs?", a: "Virtuelle IBANs teilen sich ein physisches Konto, sind aber eindeutig einem Kunden zugeordnet. Dedizierte IBANs stehen für ein eigenes Ledger-Konto." },
      { q: "Wie schnell wird ein Eingang gutgeschrieben?", a: "Bei SCT Instant in unter zehn Sekunden, inklusive Webhook-Benachrichtigung." },
      { q: "Gibt es ein Limit an virtuellen IBANs?", a: "Nein, virtuelle IBANs werden bedarfsgerecht und in großer Zahl bereitgestellt." },
      { q: "Wird der Absendername geprüft?", a: "Ja, Name-Check und Sanktionsprüfung laufen vor der Gutschrift automatisch." },
    ],
    related: ["geschaeftskonten", "zahlungen", "wallets"],
  },
  {
    slug: "wallets",
    name: "Wallets",
    category: CATEGORY,
    icon: Wallet,
    tagline: "Multi-Currency-Wallets mit Echtzeit-Ledger.",
    overline: "Wallets",
    hero: {
      title: "Wallets für jede Währung, jeden Nutzer.",
      subtitle:
        "Verwalten Sie Guthaben in mehreren Währungen mit deterministischer Ledger-Logik, Holds und automatisierten Kontenmodellen für jede Nutzergruppe.",
    },
    stats: [
      { value: "30+", label: "Währungen" },
      { value: "Atomic", label: "Transfers" },
      { value: "Holds", label: "Reservierungen" },
    ],
    features: [
      { icon: Wallet, title: "Multi-Currency", desc: "Getrennte Salden je Währung innerhalb eines Wallets, jederzeit umbuchbar." },
      { icon: Network, title: "Atomare Transfers", desc: "Wallet-zu-Wallet-Buchungen sind atomar und können nicht in Teilzuständen hängen bleiben." },
      { icon: ShieldCheck, title: "Holds & Reserven", desc: "Reservieren Sie Beträge für ausstehende Zahlungen, ohne das verfügbare Guthaben zu verändern." },
      { icon: Landmark, title: "Ledger-Wahrheit", desc: "Jede Wallet-Bewegung ist eine Ledger-Buchung – lückenlos, unveränderlich, abstimmbar." },
    ],
    architecture: {
      caption: "Wallet-Salden als Projektion des Ledgers",
      layers: [
        { label: "Aktionen", nodes: [{ label: "Top-up", sub: "inbound" }, { label: "Transfer", sub: "intern" }, { label: "Payout", sub: "outbound" }] },
        { label: "Wallet-Engine", nodes: [{ label: "Balances", sub: "je FX" }, { label: "Holds", sub: "Reserve" }, { label: "Rules", sub: "Policy" }] },
        { label: "Ledger Core", nodes: [{ label: "Double-Entry", sub: "atomic" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/wallets/:id/transfers",
      code: [
        "// Atomaren Transfer zwischen zwei Wallets buchen",
        "const tx = await cashfux.wallets.transfer({",
        '  from: "wlt_a1",',
        '  to: "wlt_b2",',
        "  amount: 25000, // in Cent",
        '  currency: "EUR",',
        "});",
        "",
        'tx.status // "settled"',
      ],
      highlights: [
        "Atomare Buchung – alles oder nichts",
        "Holds via /holds ohne Saldo-Änderung",
        "Salden pro Währung getrennt abfragbar",
      ],
    },
    useCases: [
      { title: "Marktplatz-Guthaben", desc: "Käufer- und Verkäufer-Wallets mit sofortiger interner Verrechnung." },
      { title: "Gaming & Apps", desc: "In-App-Guthaben mit Echtzeit-Salden und Auszahlungsoption." },
      { title: "B2B-Netzwerke", desc: "Verrechnungskonten zwischen Partnern ohne externe Zahlungen." },
    ],
    faq: [
      { q: "Wie werden mehrere Währungen behandelt?", a: "Ein Wallet hält getrennte Salden je Währung; Umbuchungen laufen über den FX-Dienst." },
      { q: "Was passiert bei einem Fehler mitten im Transfer?", a: "Transfers sind atomar. Entweder die vollständige Buchung geht durch oder gar nichts – kein Zwischenzustand." },
      { q: "Wofür sind Holds gedacht?", a: "Um Beträge für ausstehende Zahlungen zu reservieren, ohne das verfügbare Guthaben zu reduzieren." },
      { q: "Sind Salden in Echtzeit korrekt?", a: "Ja, Salden sind eine deterministische Projektion des Ledgers und sofort konsistent." },
    ],
    related: ["geschaeftskonten", "fx", "auszahlungen"],
  },
  {
    slug: "connected-accounts",
    name: "Connected Accounts",
    category: CATEGORY,
    icon: Network,
    tagline: "Onboarding und Verwaltung verbundener Konten.",
    overline: "Connected Accounts",
    hero: {
      title: "Verbundene Konten für Plattform-Modelle.",
      subtitle:
        "Onboarden Sie Verkäufer, Partner oder Endkunden als verbundene Konten – mit eigener Identität, eigenem Ledger und automatischem Settlement unter Ihrer Marke.",
    },
    stats: [
      { value: "White Label", label: "unter Ihrer Marke" },
      { value: "Auto", label: "Settlement" },
      { value: "Skaliert", label: "auf Tausende" },
    ],
    features: [
      { icon: Network, title: "Programmatisches Onboarding", desc: "Verbundene Konten per API erstellen, verifizieren und aktivieren." },
      { icon: ShieldCheck, title: "Delegierte Compliance", desc: "KYC/KYB je verbundenem Konto, orchestriert über den Compliance-Layer." },
      { icon: Wallet, title: "Eigener Ledger", desc: "Jedes verbundene Konto besitzt ein isoliertes Ledger mit eigenen Salden." },
      { icon: Landmark, title: "Auto-Settlement", desc: "Erlöse fließen regelbasiert auf die richtigen Konten – planbar und nachvollziehbar." },
    ],
    architecture: {
      caption: "Plattform-Kontostruktur mit verbundenen Konten",
      layers: [
        { label: "Plattform", nodes: [{ label: "Master", sub: "Ihr Konto" }, { label: "Policies", sub: "Split-Regeln" }] },
        { label: "Verbundene Konten", nodes: [{ label: "Seller A", sub: "Ledger" }, { label: "Seller B", sub: "Ledger" }, { label: "Seller C", sub: "Ledger" }] },
        { label: "Settlement", nodes: [{ label: "Payouts", sub: "geplant" }, { label: "Reports", sub: "je Konto" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/connected_accounts",
      code: [
        "// Verbundenes Konto für einen Verkäufer onboarden",
        "const acc = await cashfux.connectedAccounts.create({",
        '  business_name: "Muster Handel GmbH",',
        '  country: "DE",',
        '  capabilities: ["payments", "payouts"],',
        "});",
        "",
        'acc.status // "pending_verification"',
      ],
      highlights: [
        "KYB-Status je Konto per Webhook",
        "Capabilities granular aktivierbar",
        "Vollständig White-Label onboardbar",
      ],
    },
    useCases: [
      { title: "Marktplätze", desc: "Verkäufer onboarden, Zahlungen einziehen und automatisch auszahlen." },
      { title: "Franchise-Netzwerke", desc: "Filialen als verbundene Konten mit zentraler Steuerung." },
      { title: "Plattform-Ökonomie", desc: "Creator und Dienstleister mit eigenem Ledger und Auszahlung." },
    ],
    faq: [
      { q: "Wer trägt die Compliance-Verantwortung?", a: "Der Compliance-Layer orchestriert KYC/KYB je verbundenem Konto, eingebettet in Ihren Flow." },
      { q: "Sind die Salden isoliert?", a: "Ja, jedes verbundene Konto hat ein eigenes, getrenntes Ledger." },
      { q: "Wie funktioniert das Settlement?", a: "Regelbasiert und geplant – Erlöse werden automatisch den richtigen Konten zugeordnet und ausgezahlt." },
      { q: "Bleibt meine Marke im Vordergrund?", a: "Vollständig. Das gesamte Onboarding und die Kontoführung laufen White-Label unter Ihrer Marke." },
    ],
    related: ["geschaeftskonten", "split-payments", "kyc-compliance"],
  },
  {
    slug: "kyc-compliance",
    name: "KYC & Compliance",
    category: CATEGORY,
    icon: ShieldCheck,
    tagline: "Identitätsprüfung und regulatorische Kontrollen.",
    overline: "KYC & Compliance",
    hero: {
      title: "Compliance als Infrastruktur, nicht als Hürde.",
      subtitle:
        "KYC, KYB, Sanktions- und Transaktionsmonitoring sind tief in die Plattform eingebettet – regelkonform, auditierbar und ohne separaten Anbieter-Flickenteppich.",
    },
    stats: [
      { value: "KYC/KYB", label: "Person & Firma" },
      { value: "Real-time", label: "Screening" },
      { value: "Audit", label: "lückenlos" },
    ],
    features: [
      { icon: ShieldCheck, title: "Identitätsprüfung", desc: "KYC für natürliche Personen und KYB für Unternehmen als geführter Flow." },
      { icon: Network, title: "Sanktions-Screening", desc: "Abgleich gegen Sanktions-, PEP- und Watchlists vor jeder kritischen Aktion." },
      { icon: Landmark, title: "Transaktionsmonitoring", desc: "Regelbasierte und musterbasierte Überwachung mit Fall-Management." },
      { icon: Hash, title: "Audit-Trail", desc: "Jede Entscheidung und jeder Beleg ist unveränderlich protokolliert." },
    ],
    architecture: {
      caption: "Compliance-Layer über allen Finanzprozessen",
      layers: [
        { label: "Auslöser", nodes: [{ label: "Onboarding", sub: "KYC/KYB" }, { label: "Zahlung", sub: "Monitoring" }, { label: "Payout", sub: "Screening" }] },
        { label: "Compliance-Engine", nodes: [{ label: "Screening", sub: "Listen" }, { label: "Rules", sub: "AML" }, { label: "Cases", sub: "Review" }] },
        { label: "Nachweis", nodes: [{ label: "Audit-Log", sub: "immutable" }, { label: "Reports", sub: "Regulator" }] },
      ],
    },
    api: {
      method: "POST",
      endpoint: "/v1/kyc/verifications",
      code: [
        "// KYC-Prüfung für eine Person starten",
        "const check = await cashfux.kyc.verify({",
        '  entity: "person_77",',
        '  level: "full",',
        '  documents: ["id_front", "id_back", "selfie"],',
        "});",
        "",
        'check.status // "in_review" → "approved"',
      ],
      highlights: [
        "Webhook kyc.updated bei Statuswechsel",
        "Stufen basic | full | enhanced",
        "Belege revisionssicher gespeichert",
      ],
    },
    useCases: [
      { title: "Regulierte Plattformen", desc: "Onboarding mit vollständiger KYC/KYB-Kette ohne eigenen Compliance-Stack." },
      { title: "High-Risk-Segmente", desc: "Enhanced Due Diligence und laufendes Monitoring integriert." },
      { title: "Payout-Freigaben", desc: "Screening als Gate vor jeder Auszahlung." },
    ],
    faq: [
      { q: "Deckt ihr sowohl Personen als auch Firmen ab?", a: "Ja, KYC für natürliche Personen und KYB für Unternehmen inklusive wirtschaftlich Berechtigter." },
      { q: "Wird laufend überwacht oder nur beim Onboarding?", a: "Beides. Onboarding-Prüfung plus kontinuierliches Transaktions- und Listen-Monitoring." },
      { q: "Wie werden Nachweise gespeichert?", a: "Revisionssicher und unveränderlich mit vollständigem Audit-Trail." },
      { q: "Kann ich eigene Regeln definieren?", a: "Ja, Regelwerke und Schwellenwerte sind konfigurierbar und lösen Fälle zur Prüfung aus." },
    ],
    related: ["connected-accounts", "geschaeftskonten", "auszahlungen"],
  },
];
